import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { Truck, AlertTriangle, Ban } from '@/components/icons';
import { Badge } from '@/components/ui/Badge';
import { ItemThumb } from '@/components/ui/ItemThumb';
import { useToast } from '@/components/ui/useToast';
import { listEnquiries } from '@/api/enquiry';
import { promoteEnquiry } from '@/api/dispatchOrder';
import { formatDate, formatINR, formatINRSymbol, todayISO } from '@/lib/format';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
}

const CREDIT_TONE = { GREEN: 'success', AMBER: 'warning', RED: 'danger' } as const;

export function PromoteEnquiryModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  const [enqId, setEnqId] = useState<number | ''>('');
  const [expectedDispatch, setExpectedDispatch] = useState(todayISO());
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [salesPerson, setSalesPerson] = useState('');
  const [remarks, setRemarks] = useState('');
  const [allowPartial, setAllowPartial] = useState(false);

  // Reviewed enquiries waiting to become DOs.
  const { data: reviewedResp } = useQuery({
    queryKey: ['enquiries', { stage: 'reviewed', pageSize: 200 }],
    queryFn: () => listEnquiries({ stage: 'reviewed', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });
  const { data: newResp } = useQuery({
    queryKey: ['enquiries', { stage: 'new', pageSize: 200 }],
    queryFn: () => listEnquiries({ stage: 'new', pageSize: 200 }),
    enabled: open,
    staleTime: 30_000,
  });
  const enquiries = useMemo(
    () => [...(reviewedResp?.data ?? []), ...(newResp?.data ?? [])].sort((a, b) => b.id - a.id),
    [reviewedResp, newResp],
  );

  const selected = useMemo(
    () => enquiries.find((e) => e.id === enqId) ?? null,
    [enquiries, enqId],
  );

  useEffect(() => {
    if (!open) {
      setEnqId('');
      setExpectedDispatch(todayISO());
      setDeliveryAddress('');
      setSalesPerson('');
      setRemarks('');
      setAllowPartial(false);
      return;
    }
  }, [open]);

  useEffect(() => {
    if (selected) {
      setDeliveryAddress(selected.deliveryAddress ?? '');
      setSalesPerson(selected.salesRep);
    }
  }, [selected]);

  const isPartial = useMemo(() => {
    if (!selected) return false;
    return selected.items.some(
      (it) => it.approvedQty != null && Number(it.approvedQty) < Number(it.qty),
    );
  }, [selected]);

  const promoteMutation = useMutation({
    mutationFn: () =>
      promoteEnquiry({
        enqId: Number(enqId),
        expectedDispatch,
        deliveryAddress: deliveryAddress.trim() || undefined,
        salesPerson: salesPerson.trim() || undefined,
        remarks: remarks.trim() || undefined,
        allowPartial,
      }),
    onSuccess: (dispatch) => {
      qc.invalidateQueries({ queryKey: ['do'] });
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      qc.invalidateQueries({ queryKey: ['pick-list'] });
      const doNo = (dispatch as { doNo?: string })?.doNo;
      toast.pushStep(doNo ? `Dispatch Order ${doNo} created` : 'Dispatch Order created', {
        body: 'Stock deducted. Pick list auto-created — assign a picker next.',
        href: '/sales/pick-list',
        nextLabel: 'Open Pick List',
        tag: 'do-created',
      });
      onClose();
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Promote failed'), 'error');
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Promote Enquiry to Dispatch Order"
      size="lg"
      footer={
        <>
          <Button variant="glass" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => promoteMutation.mutate()}
            disabled={promoteMutation.isPending || !enqId}
          >
            {promoteMutation.isPending ? 'Creating…' : <><Truck size={14} className="mr-1 inline-block align-[-2px]" /> Create DO</>}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <FormGroup label="Enquiry" required>
          <Select
            value={String(enqId)}
            onChange={(e) => setEnqId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">— Pick a reviewed enquiry —</option>
            {enquiries.map((e) => (
              <option key={e.id} value={e.id}>
                {e.enqNo} · {e.party.name} · {formatINRSymbol(e.estOrderValue)}
              </option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup label="Expected Dispatch" required>
          <Input
            type="date"
            value={expectedDispatch}
            onChange={(e) => setExpectedDispatch(e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Sales Person">
          <Input
            value={salesPerson}
            onChange={(e) => setSalesPerson(e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Customer">
          <Input value={selected?.party.name ?? '—'} readOnly className="opacity-70" />
        </FormGroup>
        <FormGroup label="Delivery Address" className="col-span-2">
          <Textarea
            rows={2}
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress(e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Remarks" className="col-span-2">
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </FormGroup>
      </div>

      {selected && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Order Value</div>
              <div className="text-base font-extrabold text-accent">
                {formatINRSymbol(selected.estOrderValue)}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Items</div>
              <div className="text-base font-extrabold">{selected.items.length}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Credit</div>
              {selected.creditStatus ? (
                <Badge tone={CREDIT_TONE[selected.creditStatus]}>{selected.creditStatus}</Badge>
              ) : '—'}
            </div>
          </div>

          {isPartial && (
            <div className="rounded-md border border-warning/40 bg-warning/[0.08] px-3 py-2 text-xs text-warning">
              <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> Enquiry is partially approved (some lines have approvedQty &lt; requested).
              <label className="ml-3 inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={allowPartial}
                  onChange={(e) => setAllowPartial(e.target.checked)}
                />
                Proceed with partial DO
              </label>
            </div>
          )}

          {selected.creditStatus === 'RED' && (
            <div className="rounded-md border border-danger/40 bg-danger/[0.08] px-3 py-2 text-xs text-danger">
              <Ban size={13} className="mr-1 inline-block align-[-2px]" /> Credit RED — DO will be created in <strong>credit-pending</strong> stage. Use the
              Credit Check page to approve or block.
            </div>
          )}

          <div className="rounded-card border border-[var(--border-color)] bg-white/[0.02] p-3">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              Lines preview
            </div>
            <ul className="space-y-1 text-xs">
              {selected.items.map((it) => {
                const qty = Number(it.approvedQty ?? it.qty);
                const requested = Number(it.qty);
                return (
                  <li key={it.id} className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      <ItemThumb src={it.item?.imageUrl} size={28} />
                      <span className="min-w-0 truncate">
                        {it.itemName}
                        {it.item?.barcode && <span className="ml-1.5 text-[10px] text-[var(--text-muted)]">{it.item.barcode}</span>}
                      </span>
                    </span>
                    <span>
                      <strong>{qty}</strong> {it.unit}
                      {qty < requested && (
                        <span className="ml-2 text-[10px] text-warning">(of {requested} requested)</span>
                      )}
                      <span className="ml-2 text-accent">₹{formatINR(qty * Number(it.rate))}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 text-[10px] text-[var(--text-muted)]">
              Date: {formatDate(selected.enqDate)} · Sales Rep: {selected.salesRep}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
